import { IconX } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { AlpharunMark } from "./alpharunMark.js";

/**
 * Chip around a leading entity icon, following the playbook editor's tree rows
 * (`recordingAssessmentEditor.tsx`): a soft light square with a small dark
 * glyph. Two departures from those rows: they sit on white and tint the chip
 * gray, where this pill is already `muted` so the chip takes the lighter
 * surface to keep the same figure-on-ground step; and the whole mark is a
 * size down, since it shares a line with `text-xs` rather than a nav row.
 */
export const chatContextPillMarkClassName =
  "flex size-5 shrink-0 items-center justify-center rounded-md bg-card text-foreground";

/** Standard sizing for a Tabler icon inside `chatContextPillMarkClassName`. */
export const chatContextPillIconClassName = "size-3.5 shrink-0";

export interface ChatContextPillProps {
  /** What the context is — an automation, playbook or voice agent name. */
  title: ReactNode;
  /** Qualifies the title where a name isn't enough on its own (e.g. a run's date). */
  subtitle?: ReactNode;
  /** Replaces the default Alpharun mark. */
  icon?: ReactNode;
  onRemove?: () => void;
  className?: string;
}

/**
 * Chip describing context attached to a chat message (rendered in a composer's
 * `contextPills` slot). Sized to its content, capped at the space it's given.
 */
export const ChatContextPill: FC<ChatContextPillProps> = ({
  title,
  subtitle,
  icon,
  onRemove,
  className,
}) => (
  <div
    className={twMerge(
      // `max-w-full` rather than `w-full`: the pill hugs a short name instead
      // of running the width of the composer, and still truncates a long one.
      "inline-flex max-w-full min-w-0 items-center gap-2 rounded-lg bg-muted py-1.5 pl-1.5",
      // The leading mark is a solid block that reads as its own inset, and the
      // remove button carries padding inside its hit area — text has neither,
      // so it needs the extra room to not end flush against the edge.
      onRemove ? "pr-1.5" : "pr-2.5",
      className,
    )}
  >
    {/* Sized to match `chatContextPillMarkClassName`, so a pill carrying the
        brand mark and one carrying an entity glyph line up when stacked. */}
    {icon ?? <AlpharunMark className="bg-foreground text-background" />}

    {/* One line rather than stacked: the pill sits directly above the input,
        where a second line costs more height than a subtitle is worth. The
        title gives up its width first — subtitles are short and fixed-length
        (a date), so truncating one would be all ellipsis. */}
    <div className="flex min-w-0 items-baseline gap-1 text-xs">
      <span className="min-w-0 truncate font-medium text-foreground">
        {title}
      </span>
      {subtitle ? (
        <span className="shrink-0 text-muted-foreground">{subtitle}</span>
      ) : null}
    </div>

    {onRemove ? (
      <AriaButton
        aria-label="Remove context"
        className="flex size-5 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-foreground"
        onPress={() => onRemove()}
      >
        <IconX aria-hidden className="size-3.5" />
      </AriaButton>
    ) : null}
  </div>
);
